import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Baby, User, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { trackAgeSelectorClick } from '../../utils/tracking';

/**
 * Age selector component for homepage
 * Mobile-first design with large touch targets
 */
export const AgeSelector = () => {
  const navigate = useNavigate();

  const ageGroups = [
    {
      id: '0-2',
      label: '0-2 years',
      icon: Baby,
      description: 'Newborn & Toddler',
      color: 'from-pink-100 to-pink-50',
      textColor: 'text-pink-600',
      bgColor: 'bg-pink-100'
    },
    {
      id: '3-5',
      label: '3-5 years',
      icon: User,
      description: 'Preschool',
      color: 'from-cyan-100 to-cyan-50',
      textColor: 'text-cyan-600',
      bgColor: 'bg-cyan-100'
    },
    {
      id: '6-12',
      label: '6-12 years',
      icon: Users,
      description: 'School Age',
      color: 'from-orange-100 to-orange-50',
      textColor: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  const handleAgeSelect = (ageId) => {
    // Store in localStorage for personalization
    localStorage.setItem('selected_age', ageId);
    
    // Track analytics
    trackAgeSelectorClick(ageId);
    
    navigate(`/shop/age/${ageId}`);
  };

  return (
    <section className="py-6 md:py-8">
      <div className="text-center mb-4">
        <h2 className="text-xl md:text-2xl font-heading font-bold mb-2">
          Shop by Your Child's Age
        </h2>
        <p className="text-sm text-muted-foreground">
          Find perfect products for every stage
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        {ageGroups.map((group) => {
          const Icon = group.icon;
          return (
            <Card
              key={group.id}
              className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 hover:border-cyan-300"
              onClick={() => handleAgeSelect(group.id)}
              data-testid={`age-selector-${group.id}`}
            >
              <div className={`p-4 md:p-6 text-center bg-gradient-to-br ${group.color}`}>
                <div className={`w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 rounded-full ${group.bgColor} flex items-center justify-center`}>
                  <Icon className={`w-8 h-8 md:w-10 md:h-10 ${group.textColor}`} />
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-1">{group.label}</h3>
                <p className="text-sm text-muted-foreground mb-3">{group.description}</p>
                <Button 
                  size="sm" 
                  className={`w-full ${group.bgColor} ${group.textColor} hover:opacity-90`}
                  variant="secondary"
                >
                  Shop Now
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
};
